



















































































































import { Component, Vue } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
import { Topic } from "@/modules/Topic";
import _ from "lodash";
import TopicRepository from "@/repositories/TopicRepository";
import moment from "moment";
import SelectOwner from "@/components/owner/SelectOwner.vue";
import Owner from "@/model/cospirit-api/Owner";

interface TopicNode {
    label: string;
    uuid: string;
    isArchived: boolean;
    topic: Topic;
    children: TopicNode[];
}

interface TopicGroup {
    breadcrumb: string;
    topicsTree: TopicNode[];
}

interface TopicGroupCollection {
    [key: string]: TopicGroup;
}

const components = {
    SelectOwner,
};

@Component({components})
export default class TopicAdmin extends Vue {
    private selectedTopic: Topic|null = null;
    private lastEditedTopicUuid: string = "";
    private formVisible: boolean = false;
    private formLoading: boolean = false;
    private treeLoading: boolean = false;
    private visibleScopeTree: string|null = null;

    @Getter("getTopics") public topics!: (parentUuid: string | null) => Topic[];
    @Getter("getAllTopics") public allTopics!: Topic[];
    @Getter("getMasterOwners") public owners!: Owner[];
    @Getter("getCurrentOwnerUuid") public ownerUuid!: string;
    @Getter("hasFeature") public hasFeature!: (feature: string) => boolean;

    @Action("saveTopic") public saveTopic!: (topic: Topic) => Promise<object>;
    @Action("deleteTopic") public deleteTopic!: (topic: Topic) => Promise<object>;

    private topicsTree(topics: Topic[]): TopicNode[] {
        return _.reduce(
            _.filter(
                topics,
                (topic: Topic) => {
                    return _.get(topic, "parent.uuid", null) === null;
                }
            ),
            (tree: TopicNode[], topic: Topic): TopicNode[] => {
                const parentNode: TopicNode = this.createTopicNode(topic);

                // Add children
                _.forEach(this.topics(topic.uuid), (child: Topic) => {
                    parentNode.children.push(this.createTopicNode(child));
                });

                tree.push(parentNode);

                return tree;
            },
            []
        );
    }

    private createTopicNode(topic: Topic): TopicNode {
        return {
            label: topic.title + (topic.minAmount ? "  (" + topic.minAmount + " €)" : ""),
            uuid: topic.uuid,
            isArchived: !!_.get(topic, "archivedAt", null),
            topic,
            children: [],
        };
    }

    private isParent(node: TopicNode): boolean {
        return !_.has(node, "topic.parent.uuid");
    }

    private isHidden(node: TopicNode): boolean {
        return node.isArchived;
    }

    private isLastEdited(node: TopicNode): boolean {
        return node.uuid === this.lastEditedTopicUuid;
    }

    private selectTopic(topicNode: TopicNode) {
        this.selectedTopic = _.cloneDeep(topicNode.topic);
    }

    private edit(topicNode: TopicNode) {
        this.selectTopic(topicNode);
        this.formVisible = true;
    }

    private deleteTopics(topicNode: TopicNode) {
        if (!confirm(_.invoke(this.$options.filters, "trans", "confirmThematicSupression"))) {
            return;
        }
        this.treeLoading = true;

        const deleteChildTopics = (children: TopicNode[]): Promise<any> => {
            const deletePromises = children.map((child: TopicNode) => this.deleteTopic(child.topic));
            return Promise.all(deletePromises);
        };

        const deleteParentTopic = (topic: Topic): Promise<any> => {
            return this.deleteTopic(topic);
        };

        if (topicNode) {
            deleteChildTopics(topicNode.children)
                .then(() => deleteParentTopic(topicNode.topic))
                .then(() => {
                    this.$message({
                        showClose: true,
                        duration: 10000,
                        message: _.invoke(this.$options.filters, "trans", "deleteTopicSuccess"),
                        type: "success"
                    });
                })
                .catch(() => {
                    const errorMessage = topicNode.children.length > 0 ? "deleteTopicError" : "deleteSubTopicError";

                    this.$message({
                        showClose: true,
                        duration: 10000,
                        message: _.invoke(this.$options.filters, "trans", errorMessage),
                        type: "error"
                    });
                })
                .finally(() => {
                    this.treeLoading = false;
                });
        }
    }

    private append(topicNode: TopicNode|null) {
        this.formLoading = true;
        const defaultData = {
            owner: {
                uuid: this.ownerUuid,
            },
            scopeOwner: {
              uuid: this.ownerUuid,
            },
        };

        if (topicNode) {
            _.set(defaultData, "parent.uuid", topicNode.uuid);
            _.set(defaultData, "scopeOwner.uuid", _.get(topicNode, "topic.scopeOwner.uuid", null));
        }

        TopicRepository.create({ data: { owner: { uuid: this.ownerUuid }}}, defaultData).then((data: object) => {
            this.selectedTopic = data as Topic;
            this.formLoading = false;
        });
        this.formVisible = true;
    }

    private toggleArchive(topicNode: TopicNode) {
        const topic = _.cloneDeep(topicNode.topic);
        const isArchived = !!_.get(topic, "archivedAt");

        if (isArchived) {
            topic.archivedAt = null;
        } else {
            topic.archivedAt = moment().format("DD-MM-YYYY");
        }

        this.treeLoading = true;
        this.saveTopic(topic).then(() => {
            this.treeLoading = false;
        });
    }

    private handleFormSubmit() {
        if (this.selectedTopic) {
            this.treeLoading = true;
            this.saveTopic(this.selectedTopic).then((data: any) => {
                this.lastEditedTopicUuid = _.get(data, "uuid", "");
                this.treeLoading = false;
            }).catch((reason) => {
                this.$message({
                    showClose: true,
                    duration: 0,
                    message: _.invoke(this.$options.filters, "trans", reason.message),
                    type: "error"
                });
                this.treeLoading = false;
            });
        }
        this.formVisible = false;
    }

    private handleFormCancel() {
        this.formVisible = false;
        this.selectedTopic = null;
    }

    private get groupedByScopeTopics(): TopicGroupCollection {
        const topicGroups: TopicGroupCollection = {};
        const groupedTopics = _.groupBy(this.allTopics, (topic: Topic) => {
            return _.get(topic, "scopeOwner.uuid", null);
        });
        _.forEach(groupedTopics, (topics: Topic[], scopeOwnerUuid: string|null) => {
            if (scopeOwnerUuid) {
                topicGroups[scopeOwnerUuid] = {
                    breadcrumb: this.getOwnerBreadcrumb(scopeOwnerUuid),
                    topicsTree: this.topicsTree(topics),
                };
            }
        });

        const scopeOwnerUuids = _.keys(topicGroups);
        if (scopeOwnerUuids.length === 1) {
            this.visibleScopeTree = _.keys(topicGroups)[0];
        }

        return topicGroups;
    }

    private getOwnerBreadcrumb(ownerUuid: string): string {
        const owner = _.find(this.owners, { uuid: ownerUuid });
        let breadcrumb = _.get(owner, "fullName", "???");

        const code = _.get(owner, "clientCode1", false);
        if (code) {
            breadcrumb = breadcrumb +  " - " + code;
        }
        const city = _.get(owner, "address.city", false);
        if (city) {
            breadcrumb = breadcrumb + " (" + city + ")";
        }

        const parentUuid = _.get(owner, "parent.uuid", null);
        if (parentUuid) {
            breadcrumb = this.getOwnerBreadcrumb(parentUuid) + " > " + breadcrumb;
        }

        return breadcrumb;
    }

    private get canSave(): boolean {
        return this.selectedTopic?.title !== "";
    }
}
